import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  inject,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { ConsentService, CONSENT_SERVICES } from '@modules/consent/consent.service';

/**
 * Consent overlay component for embedded services like Google Maps and YouTube
 *
 * This component needs to be shown instead of the embedded service, if the user has not consented yet.
 * If the user consents to a service, UserCentrics will trigger an event,
 * which the implementing component needs to subscribe to, in order to show or hide the embedded service.
 */
@Component({
  selector: 'mb-consent-overlay',
  templateUrl: './consent-overlay.component.html',
  styleUrls: ['./consent-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, TranslatePipe],
})
export class ConsentOverlayComponent implements OnInit, OnDestroy {
  private consentService = inject(ConsentService);
  private cdr = inject(ChangeDetectorRef);

  private destroy$ = new Subject<boolean>();
  /**
   * The service, the user should consent to
   */
  @Input()
  providerConfig!: CONSENT_SERVICES;

  @Input()
  backgroundImageUrl!: string;

  // the template to render after consent is true
  @ContentChild(TemplateRef) template: any;

  public hasConsent: Boolean = false;
  public translationKey: any = {
    [CONSENT_SERVICES.YOUTUBE]: 'YOUTUBE_VIDEO',
    [CONSENT_SERVICES.HERE]: 'HERE',
  };

  public ngOnInit() {
    this.hasConsent = this.consentService.consentedTo(this.providerConfig);
    this.consentService.changedConsent.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.hasConsent = data[this.providerConfig];
      this.cdr.detectChanges();
    });
  }

  /**
   * User clicked on "consent"
   */
  public consent(): void {
    if (this.providerConfig) {
      this.consentService.consentTo(this.providerConfig);
    }
  }

  /**
   * User clicked on "more info"
   */
  public showInfo(): void {
    this.consentService.showInfoModal();
  }

  public ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
